import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Typography,
  Box,
  useTheme,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  Button,
  ListItemText,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import TableViewIcon from "@mui/icons-material/TableView";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GetAppIcon from "@mui/icons-material/GetApp";
import * as XLSX from "xlsx";
//for pdf Export
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const LastMonthReportDialog = ({ open, onClose, groupId, groupName }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/devices?groupId=${groupId}`
      );
      const unitsData = response.data || [];
      const unitIds = unitsData.map((unit) => unit.id);
      const nameMap = unitsData.reduce((acc, u) => {
        acc[u.id] = u.name;
        return acc;
      }, {});
      if (unitIds.length === 0) {
        setError("No units found for this group.");
        return;
      }
      const payload = {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
        units: unitIds,
      };
      const reportResponse = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/reports/precision-report-month`,
        payload,
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        }
      );
      const data = reportResponse.data?.data?.mydict || {};
      const parsed = Object.entries(data).map(([unitId, values]) => ({
        unitId,
        name: nameMap[unitId] || unitId,
        ...values,
      }));

      setReportData(parsed);
    } catch (err) {
      console.error("Error fetching last month report:", err);
      setError("Failed to load report data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !groupId) return;
    fetchReportData();
  }, [open, groupId]);

  const filteredData = reportData.filter((row) =>
    row.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const copyToClipboard = () => {
    if (!filteredData || filteredData.length === 0) {
      console.warn("No report data to copy");
      toast.warning("No report data available to copy");
      return;
    }
    const textContent = filteredData
      .map((item) => {
        return `${item.name}\t${item.year}\t${item.month}\t${item.down}\t${item.up}\t${item.customer}\t${item.precision}\t${item.shutDowns}`;
      })
      .join("\n");

    const headers =
      "Unit Name\tYear\tMonth\tStop(Hrs)\tRun(Hrs)\tCustomer(Hrs)\tPRC(Hrs)\tShutdowns";

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => toast.success(`Monthly report copied to clipboard`))
      .catch((err) => console.error("Failed to copy:", err));

    handleExportMenuClose();
  };

  const exportToCSV = () => {
    if (!filteredData || filteredData.length === 0) {
      console.warn("No report data to export");
      toast.warning("No report data available to export");
      return;
    }
    const csvContent = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Stop (Hrs)",
        "Run (Hrs)",
        "Customer (Hrs)",
        "PRC (Hrs)",
        "Shutdowns",
      ],
      ...filteredData.map((item) => [
        item.name,
        item.year,
        item.month,
        item.down,
        item.up,
        item.customer,
        item.precision,
        item.shutDowns,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Last_Monthly_Report.csv`;
    link.click();

    handleExportMenuClose();
    toast.success("Exported Monthly Report to CSV");
  };

  const exportToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      console.warn("No report data to export");
      toast.warning("No report data available to export");
      return;
    }

    const headers = [
      "Unit Name",
      "Year",
      "Month",
      "Stop (Hrs)",
      "Run (Hrs)",
      "Customer (Hrs)",
      "PRC (Hrs)",
      "Shutdowns",
    ];
    const rows = filteredData.map((item) => [
      item.name,
      item.year,
      item.month,
      item.down,
      item.up,
      item.customer,
      item.precision,
      item.shutDowns,
    ]);

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly Report");

    // Export file
    XLSX.writeFile(workbook, `Last_Monthly_Report.xlsx`);

    handleExportMenuClose();
    toast.success("Exported Monthly Report to Excel");
  };

  const exportToPDF = () => {
    if (!filteredData || filteredData.length === 0) {
      console.warn("No report data to export");
      toast.warning("No report data available to export");
      return;
    }

    const doc = new jsPDF("l", "pt", "a4"); // landscape, points, A4

    // Title
    doc.setFontSize(12);
    doc.text("Monthly Units Report", 40, 30);

    // Table headers
    const headers = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Stop (Hrs)",
        "Run (Hrs)",
        "Customer (Hrs)",
        "PRC (Hrs)",
        "Shutdowns",
      ],
    ];

    // Table rows from filteredReportData
    const rows = filteredData.map((item) => [
      item.name,
      item.year,
      new Date(item.year, item.month - 1).toLocaleString("default", {
        month: "long",
      }),
      item.down,
      item.up,
      item.customer,
      item.precision,
      item.shutDowns,
    ]);

    // Render table with autoTable
    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: "linebreak",
      },
      headStyles: { fillColor: [41, 128, 185] },
      tableWidth: "auto",
      columnStyles: {
        0: { cellWidth: 90 }, // Unit Name
        1: { cellWidth: 50 }, // Year
        2: { cellWidth: 60 }, // Month
        3: { cellWidth: 70 }, // Stop (Hrs)
        4: { cellWidth: 70 }, // Run (Hrs)
        5: { cellWidth: 90 }, // Customer (Hrs)
        6: { cellWidth: 70 }, // PRC (Hrs)
        7: { cellWidth: 70 }, // Shutdowns
      },
    });

    // Save file
    doc.save(`Monthly_Report.pdf`);
    handleExportMenuClose();
    toast.success(`Exported Monthly Report to PDF`);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" component="span">
          {groupName ? `Last Month Report - ${groupName}` : "Last Month Report"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={1}
          my={2}
          justifyContent={"space-between"}
          size={{ xs: 12, md: 6 }}
        >
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search unit..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            sx={{ borderRadius: 0 }}
            variant="outlined"
            color="white"
            startIcon={<GetAppIcon />}
            onClick={handleExportMenuOpen}
          >
            Export
          </Button>
        </Grid>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center" sx={{ py: 2 }}>
            {error}
          </Typography>
        ) : filteredData.length === 0 ? (
          <Typography align="center" sx={{ py: 2 }}>
            No report data available
          </Typography>
        ) : (
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                {[
                  "Unit Name",
                  "Year",
                  "Month",
                  "Stop(Hrs)",
                  "Run(Hrs)",
                  "Customer(Hrs)",
                  "PRC(Hrs)",
                  "Shutdowns",
                ].map((header) => (
                  <TableCell key={header}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row, index) => (
                <TableRow
                  key={row.unitId}
                  hover
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    "&:hover": {
                      backgroundColor: "#4a4d54",
                    },
                  }}
                >
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.year}</TableCell>
                  <TableCell>
                    {new Date(row.year, row.month - 1).toLocaleString(
                      "default",
                      {
                        month: "long",
                      }
                    )}
                  </TableCell>
                  <TableCell>{row.down}</TableCell>
                  <TableCell>{row.up}</TableCell>
                  <TableCell>{row.customer}</TableCell>
                  <TableCell>{row.precision}</TableCell>
                  <TableCell>{row.shutDowns}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
      >
        <MenuItem onClick={copyToClipboard}>
          <ListItemIcon>
            <FileCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToCSV}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToExcel}>
          <ListItemIcon>
            <TableViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Excel</ListItemText>
        </MenuItem>
        <MenuItem onClick={exportToPDF}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>PDF</ListItemText>
        </MenuItem>
      </Menu>
    </Dialog>
  );
};

export default LastMonthReportDialog;
